import React, { useState, useEffect } from "react";
import Page from "../../../../../components/Page";
import { Box, Divider, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import Header from "./Header";
import {
  getAccountUsers,
  updateUserByAdmin,
  deleteUserByAdmin,
  getRolesAndAccess,
} from "../../../../../core/repo/accountRepo";
import { useDispatch } from "react-redux";
import Filters from "./Filters";
import Results from "./Results/Results";
import Loader from "../../../../../components/Loader";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 8,
    background: theme.palette.background.dark,
  },
  container: {
    borderRadius: 24,
    border: `1px solid ${theme.palette.divider}`,
    width: "100%",
    height: "100%",
    background: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
}));

export const sortOptions = [
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
  { value: "role_asc", label: "Role (A-Z)" },
  { value: "role_desc", label: "Role (Z-A)" },
  { value: "status_asc", label: "Account Status (A-Z)" },
  { value: "status_desc", label: "Account Status (Z-A)" },
  { value: "plant_asc", label: "Plant (A-Z)" },
  { value: "plant_desc", label: "Plant (Z-A)" },
  { value: "line", label: "Line" },
];

export function getSortedFiles(items, sortOption) {
  if (!items) return [];

  const sortedItems = [...items].sort((item1, item2) => {
    switch (sortOption) {
      case "name_asc": {
        return item1.user?.name.localeCompare(item2.user?.name);
      }
      case "name_desc": {
        return item2.user?.name.localeCompare(item1.user?.name);
      }
      case "role_asc": {
        return item1.user?.role.localeCompare(item2.user?.role);
      }
      case "role_desc": {
        return item2.user?.role.localeCompare(item1.user?.role);
      }
      case "status_asc": {
        return item1.user?.status.localeCompare(item2.user?.status);
      }
      case "status_desc": {
        return item2.user?.status.localeCompare(item1.user?.status);
      }
      case "plant_asc": {
        return item1.user?.plant.localeCompare(item2.user?.plant);
      }
      case "plant_desc": {
        return item2.user?.plant.localeCompare(item1.user?.plant);
      }
      case "line": {
        return item1.user?.line.localeCompare(item2.user?.line);
      }
      // case "created_at": {
      //   return Date.parse(item2.created_at) - Date.parse(item1.created_at);
      // }
      // case "updated_at": {
      //   return Date.parse(item2.updated_at) - Date.parse(item1.updated_at);
      // }
      default:
        return 0;
    }
  });

  return sortedItems;
}

function Users() {
  const { enqueueSnackbar } = useSnackbar();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortOption, setSortOption] = useState(sortOptions[0].value);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [plant, setPlant] = useState("all");
  const [line, setLine] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(getAccountUsers());
      setUsers(response["users"] || []);
      setFilteredUsers(response["users"] || []);
      const rolesResponse = await dispatch(getRolesAndAccess());
      setRoles(rolesResponse);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const updateUrlParams = (updates) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "All") {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });

    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleSortFiles = (option) => {
    setSortOption(option);
    updateUrlParams({ sort: option });
    const sortedUsers = getSortedFiles(filteredUsers, option);
    setFilteredUsers(sortedUsers);
  };

  const handleSearchFiles = (query) => {
    setQuery(query);
    const filteredUsers = users.filter((user) => {
      const { name, email, role, plant, line, status } = user;
      return (
        name?.toLowerCase().includes(query.toLowerCase()) ||
        email?.toLowerCase().includes(query.toLowerCase()) ||
        role?.toLowerCase().includes(query.toLowerCase()) ||
        plant?.toLowerCase().includes(query.toLowerCase()) ||
        line?.toLowerCase().includes(query.toLowerCase()) ||
        status?.toLowerCase().includes(query.toLowerCase())
      );
    });
    setFilteredUsers(filteredUsers);
  };

  const handleStatusChange = (status) => {
    setStatus(status);
    updateUrlParams({ status });
  };

  const handlePlantChange = (plant) => {
    setPlant(plant);
    updateUrlParams({ plant });
  };

  const handleLineChange = (line) => {
    setLine(line);
    updateUrlParams({ line });
  };

  const handleResetFilters = () => {
    setQuery("");
    setStatus("active");
    setPlant("all");
    setLine("all");
    updateUrlParams({});
    setFilteredUsers(users);
  };

  const handleUpdateUser = async (userId, user) => {
    try {
      await dispatch(updateUserByAdmin(userId, user));
      const updatedUsers = users.map((u) => {
        if (u.id === userId) {
          return { ...u, ...user };
        }
        return u;
      });
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      enqueueSnackbar("User updated successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to update user", {
        variant: "error",
      });
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await dispatch(deleteUserByAdmin(userId));
      const updatedUsers = users.filter((user) => user.id !== userId);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      enqueueSnackbar("User removed successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to remove user", {
        variant: "error",
      });
      throw error;
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
  };

  return (
    <Page title="Users" className={classes.root}>
      <Box className={classes.container} position="relative">
        <Header />
        <Divider />
        {isLoading ? (
          <Loader />
        ) : (
          <Box p={3} flex={1} overflow="auto">
            <Filters
              sortOption={sortOption}
              query={query}
              status={status}
              plant={plant}
              onSortFiles={handleSortFiles}
              onSearchFiles={handleSearchFiles}
              line={line}
              onStatusChange={handleStatusChange}
              onPlantChange={handlePlantChange}
              onLineChange={handleLineChange}
              users={users}
              onResetFilters={handleResetFilters}
            />
            <Box mb={3} />
            <Results
              users={filteredUsers}
              handleUpdateUser={handleUpdateUser}
              handleDeleteUser={handleDeleteUser}
              roles={roles}
              selectedUser={selectedUser}
              handleSelectUser={handleSelectUser}
            />
          </Box>
        )}
      </Box>
    </Page>
  );
}

export default Users;
